import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { ArrowLeft } from 'react-feather';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import FairAdminRequests from '../Components/FairOrganizations/FairAdminRequests';
import FairDetailsModal from '../Components/FairOrganizations/FairDetailsModal';
import FairSeasonModal from '../Components/FairOrganizations/FairSeasonModal';
import SeasonExhibitors from '../Components/FairOrganizations/SeasonExhibitors';
import UserManagementUsersTab from '../Components/UserManagementUsersTab';
import { getFairProfile, getFairSeasons } from '../ReduxToolkit/Reducers/fairManagement';
import { getCamelCaseWord } from '../static/brands_wholesalers_data';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '--');

// Organization Management > Fair Organizations > eye: the fair's details, seasons and each
// season's exhibitors, with full edit access (the venue and images stay the fair's own)
const FairOrganizationDetail = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // null = closed, {} = create, a season = edit
  const [seasonModal, setSeasonModal] = useState(null);

  const loadProfile = useCallback(async () => {
    const data = await dispatch(getFairProfile(organizationId));
    if (data) setProfile(data);
  }, [dispatch, organizationId]);

  const loadSeasons = useCallback(async () => {
    const data = await dispatch(getFairSeasons(organizationId));
    if (!data) return;
    setSeasons(data);
    // Keep the picked season; else the ongoing one, else the newest
    setSeasonId((current) =>
      data.some((s) => s.id === current)
        ? current
        : (data.find((s) => s.is_ongoing) || data[0])?.id ?? null
    );
  }, [dispatch, organizationId]);

  useEffect(() => {
    loadProfile();
    loadSeasons();
  }, [loadProfile, loadSeasons]);

  const season = seasons.find((s) => s.id === seasonId);

  const infoItems = [
    { label: 'Organization ID', value: profile?.id || organizationId },
    { label: 'Name', value: profile?.name || '--' },
    { label: 'Website', value: profile?.website || '--' },
    {
      label: 'Markets (New Profiles countries)',
      value: profile?.countries?.map((c) => getCamelCaseWord(c)).join(', ') || '--',
    },
    { label: 'Founded year', value: profile?.founded_year || '--' },
    { label: 'Primary audience', value: profile?.primary_audience || '--' },
    { label: 'Exhibition categories', value: profile?.categories?.join(', ') || '--' },
    { label: 'Facebook', value: profile?.facebook || '--' },
    { label: 'Instagram', value: profile?.instagram || '--' },
    { label: 'LinkedIn', value: profile?.linkedin || '--' },
    { label: 'Joined', value: formatDate(profile?.created_at) },
  ];

  const seasonColumns = [
    { name: 'Season', selector: (row) => row.season_name, wrap: true, grow: 2 },
    { name: 'Year', selector: (row) => row.year, width: '80px' },
    {
      name: 'Dates',
      selector: (row) =>
        row.start_date ? `${formatDate(row.start_date)} - ${formatDate(row.end_date)}` : '--',
      wrap: true,
    },
    {
      name: 'Status',
      width: '100px',
      cell: (row) => (row.is_ongoing ? <Badge color="success">Ongoing</Badge> : null),
    },
    {
      name: 'Venue',
      selector: (row) => row.fair_location?.formatted_address || row.fair_location?.name || '--',
      wrap: true,
      grow: 2,
    },
    { name: 'Exhibitors', selector: (row) => row.exhibitors, width: '110px' },
    { name: 'Registered', selector: (row) => row.registered, width: '110px' },
    {
      name: '',
      width: '90px',
      cell: (row) => (
        <Button
          color="primary"
          size="sm"
          outline
          onClick={(e) => {
            e.stopPropagation();
            setSeasonModal(row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Container fluid style={{ padding: '20px' }}>
      <button
        type="button"
        className="btn btn-link text-decoration-none p-0 mb-3 d-flex align-items-center gap-2"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        Back to Fair Organizations
      </button>
      <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
        <h3 className="mb-0">{profile?.name || 'Fair organization'}</h3>
        <Badge color="info" className="text-uppercase">
          Fair
        </Badge>
      </div>

      <Card className="shadow-sm mb-4">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">Fair details</h5>
            <Button color="primary" outline disabled={!profile} onClick={() => setDetailsOpen(true)}>
              Edit details
            </Button>
          </div>
          <Row className="g-3">
            {infoItems.map((item) => (
              <Col key={item.label} xs={12} sm={6} md={4} lg={3}>
                <div className="h-100 p-3 border rounded" style={{ backgroundColor: '#fafafa' }}>
                  <small className="text-muted d-block mb-1">{item.label}</small>
                  <div className="fw-semibold text-break">{item.value}</div>
                </div>
              </Col>
            ))}
          </Row>
        </CardBody>
      </Card>

      <FairAdminRequests organizationId={organizationId} />

      <Card className="shadow-sm mb-4">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">Seasons</h5>
            <Button color="primary" onClick={() => setSeasonModal({})}>
              Create season
            </Button>
          </div>
          <DataTable
            data={seasons}
            columns={seasonColumns}
            pointerOnHover
            highlightOnHover
            onRowClicked={(row) => setSeasonId(row.id)}
            conditionalRowStyles={[
              { when: (row) => row.id === seasonId, style: { backgroundColor: '#eef4ff' } },
            ]}
            noDataComponent={<div className="p-4 text-muted">No seasons yet</div>}
          />
        </CardBody>
      </Card>

      {season && (
        <SeasonExhibitors
          key={season.id}
          organizationId={organizationId}
          season={season}
          onChanged={loadSeasons}
        />
      )}

      <h5 className="mb-3">Users</h5>
      {profile && <UserManagementUsersTab organizationName={profile.name} embedded />}

      <FairDetailsModal
        open={detailsOpen}
        profile={profile}
        onClose={() => setDetailsOpen(false)}
        onSaved={setProfile}
      />
      <FairSeasonModal
        open={!!seasonModal}
        organizationId={organizationId}
        season={seasonModal?.id ? seasonModal : null}
        onClose={() => setSeasonModal(null)}
        onSaved={async (saved) => {
          await loadSeasons();
          setSeasonId(saved.id);
        }}
      />
    </Container>
  );
};

export default FairOrganizationDetail;
